import {texture, json} from "p3";
import { Emitter } from "pixi-particles";
import common from "../common";
import definitions from "../definitions";
import GameScene from "../scenes/GameScene";
import settings from "../settings";
import ActionButton from "./ActionButton";
import CoinCounterPanel from "./CoinCounterPanel";
import Compass from "./compass/Compass";
import DialogSystem from "./DialogSystem";
import HealthBar from "./health/HealthBar";
import ItemPopup from "./ItemPopup";
import MinigameTracker from "./tracker/minigame/MinigameTracker";
import QuestTracker from "./tracker/QuestTracker";
import TutorialControls from "./tutorial/TutorialControls";
import TutorialPointer from "./tutorial/TutorialPointer";
import VirtualJoystick from "./VirtualJoystick";

export default class HUD extends PIXI.Container {
    constructor() {
        super();

        //this.buttonMargin = new PIXI.Rectangle(60, 40, 60, 50);
        //this.buttonSpacing = 120;
        this.buttonMargin = new PIXI.Rectangle(15, 30, 60, 50);
        this.buttonSpacing = 90;

        this._versionLabel = null;

        this._healthBar = null;
        this._questTracker = null;
        this._minigameTracker = null;

        this._itemPopup = null;

        this._buttonLayout = definitions.HUD_BUTTON_LAYOUT.DEFAULT;

        this._actionButton = null;
        this._pauseButton = null;

        this._defaultOverlay = null;
        this._minigameOverlay = null;

        this._mapButton = null;
        this._questButton = null;
        this._inventoryButton = null;
        this._shopButton = null;
        this._coinHolder = null;
        this._surrenderButton = null;
        this._tutorialSkipButton = null;

        //this._buttonAttractFx = null;

        this._virtualJoystick = null;

        this._tutorialPointer = null;
        this._tutorialKeyboard = null;
        this._tutorialShopHighlight = null;

        this._joystickBlockers = [];

        this._dialogSystem = null;

        this._compass = null;

        this._uiHorizontal = false;

        this.init();
    }

    init() {
        /*
        this._versionLabel = new PIXI.BitmapText("Pre-Gold", {
            fontName: "Alghera Pro",
            fontSize: 40,
            align: "left",
            tint: 0xFFFFFF
        });
        this._versionLabel.anchor.set(0, 1);
        this.addChild(this._versionLabel);
        */

        this._healthBar = new HealthBar();
        this.addChild(this._healthBar);

        this._questTracker = new QuestTracker();
        this.addChild(this._questTracker);

        this._minigameTracker = new MinigameTracker();
        this._minigameTracker.visible = false;
        this.addChild(this._minigameTracker);

        this._itemPopup = new ItemPopup();
        this.addChild(this._itemPopup);

        this._actionButton = new ActionButton();
        this.addChild(this._actionButton);

        this._mapButton = P3.Button.create(texture("btn_map"));
        this._mapButton.id = 'mapButton';
        this._mapButton.overSoundName = "sfx_btn_generic_rollover";
        this._mapButton.clickSoundName = "sfx_btn_map_open";
        this._mapButton.defaultSize = {width:this._mapButton.width, height:this._mapButton.height};
        this._mapButton.scale.set(0.9);
        this.addChild(this._mapButton);

        this._questButton = P3.Button.create(texture("btn_quests"));
        this._questButton.id = 'questButton';
        this._questButton.overSoundName = "sfx_btn_generic_rollover";
        this._questButton.clickSoundName = "sfx_btn_quests_open";
        this._questButton.defaultSize = {width:this._questButton.width, height:this._questButton.height};
        this._questButton.scale.set(0.9);
        this.addChild(this._questButton);

        this._inventoryButton = P3.Button.create(texture("btn_inventory"));
        this._inventoryButton.id = 'inventoryButton';
        this._inventoryButton.overSoundName = "sfx_btn_generic_rollover";
        this._inventoryButton.clickSoundName = "sfx_btn_bag_open";
        this._inventoryButton.defaultSize = {width:this._inventoryButton.width, height:this._inventoryButton.height};
        this._inventoryButton.scale.set(0.9);
        this.addChild(this._inventoryButton);

        const mainButtons = [
            this._mapButton, this._questButton, this._inventoryButton
        ];

        if (settings.PHASE >= 2) {
            this._shopButton = P3.Button.create(texture("btn_shop"));
            this._shopButton.id = 'shopButton';
            this._shopButton.overSoundName = "sfx_btn_generic_rollover";
            this._shopButton.clickSoundName = "sfx_btn_bag_open";
            this._shopButton.defaultSize = {width:this._shopButton.width, height:this._shopButton.height};
            this._shopButton.scale.set(0.9);
            this.addChild(this._shopButton);

            mainButtons.push(this._shopButton);

            this._coinHolder = new CoinCounterPanel();
            this._coinHolder.x = 5;
            this._coinHolder.y = this._shopButton.height / 2;
            this._coinHolder.coinAmount = common.saveData.activeSlot.coins;
            this._coinHolder.scale.set(0.7);
            this._shopButton.addChild(this._coinHolder);
        }

        for (let i in mainButtons) {
            if (common.saveData.activeSlot.unlocks.indexOf(mainButtons[i].id) < 0) {
                mainButtons[i].attractFx = this._createFx(mainButtons[i], "fx_button_attract_00", [
                    texture("button_sparkle_attract_fx_00"), 
                    texture("button_sparkle_attract_fx_01")
                ]);
                mainButtons[i].attractFx.emit = false;
            }
        }

        this._surrenderButton = P3.Button.create(texture("btn_small_red_out"), null, null, texture("btn_icon_surrender"));
        this._surrenderButton.overSoundName = "sfx_btn_generic_rollover";
        this._surrenderButton.clickSoundName = "sfx_btn_generic_press_00";
        this._surrenderButton.defaultSize = {width:this._surrenderButton.width, height:this._surrenderButton.height};
        this._surrenderButton.visible = false;
        this.addChild(this._surrenderButton);

        this._tutorialSkipButton = P3.Button.create(texture("btn_action_medium_out"), null, null, texture("btn_icon_skip_medium"));
        this._tutorialSkipButton.overSoundName = "sfx_btn_generic_rollover";
        this._tutorialSkipButton.clickSoundName = "sfx_btn_generic_press_00";
        this._tutorialSkipButton.defaultSize = {width:this._tutorialSkipButton.width, height:this._tutorialSkipButton.height};
        this._tutorialSkipButton.visible = false;
        this.addChild(this._tutorialSkipButton);

        this._pauseButton = P3.Button.create(
            texture("btn_small_out"),
            texture("btn_small_out"),
            null,
            texture("btn_icon_pause")
        );
        this._pauseButton.overSoundName = "sfx_btn_generic_rollover";
        this._pauseButton.clickSoundName = "sfx_btn_generic_press_00";
        this._pauseButton.defaultSize = {width:this._pauseButton.width, height:this._pauseButton.height};
        this.addChild(this._pauseButton);

        this._dialogSystem = new DialogSystem();
        this.addChild(this._dialogSystem);

        this._virtualJoystick = new VirtualJoystick();
        this.addChild(this._virtualJoystick);

        this._tutorialPointer = new TutorialPointer();
        this.addChild(this._tutorialPointer);

        this._tutorialControls = new TutorialControls();
        this.addChild(this._tutorialControls);
        
        this._joystickBlockers = [
            this._actionButton,
            this._mapButton,
            this._questButton,
            this._inventoryButton,
            this._pauseButton,
            this._tutorialSkipButton
        ];

        if (this._shopButton) {
            this._joystickBlockers.push(this._shopButton);
        }

        if (settings.PHASE > 1 && common.saveData.activeSlot.purchases && common.saveData.activeSlot.purchases.item_compass) {
            this.addCompass();
        }

        this.resize();
    }

    destroy() {
        super.destroy({ children: true });
    }

    resize() {
        if (this._versionLabel) {
            this._versionLabel.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5 + 20, P3.View.height - 20);
        }
        this._dialogSystem.position.set(common.STAGE_WIDTH * 0.5, 0);
        this._healthBar.position.set(common.STAGE_WIDTH * 0.5, 0);
        this._questTracker.position.set(common.STAGE_WIDTH * 0.5, this.buttonMargin.y + 60);
        this._minigameTracker.position.set(common.STAGE_WIDTH * 0.5, this.buttonMargin.y + 60);
        this._itemPopup.position.set(common.STAGE_WIDTH * 0.5, 640);

        this._tutorialPointer.position.set(common.STAGE_WIDTH * 0.5, P3.View.height * 0.5);
        this._tutorialControls.position.set(common.STAGE_WIDTH * 0.5 - 280, P3.View.height * 0.5 + 40);

        this._actionButton.position.set(
            (common.STAGE_WIDTH + P3.View.width - this._actionButton.width) * 0.5 - this.buttonMargin.width,
            P3.View.height - this._actionButton.height * 0.5 - this.buttonMargin.height
        );

        let horizontal = false;

       // console.log('DEVICE SCREEN RATIO', P3.View.width / P3.View.height);

        if (P3.View.width / P3.View.height > 1.8) {
            horizontal = true;
        }

        const topButtons = [this._mapButton, this._questButton, this._inventoryButton];
        if (this._shopButton) {
            if (horizontal) {
                topButtons.unshift(this._shopButton);
            } else {
                topButtons.push(this._shopButton);
            }
        }
        
        for (let i = 0; i < topButtons.length; i++) {
            if (horizontal) {
                topButtons[i].position.set(
                    ((common.STAGE_WIDTH - P3.View.width + this._mapButton.defaultSize.width) * 0.5) + ((this.buttonSpacing*0.97)*i) + this.buttonMargin.x,
                    (this._mapButton.defaultSize.height * 0.5) + (this.buttonMargin.y * 0.3)
                );
                if (i > 0 ) {
                    topButtons[i].x += 10;
                }
            } else {
                topButtons[i].position.set(
                    (common.STAGE_WIDTH - P3.View.width + this._mapButton.defaultSize.width) * 0.5,
                    this._mapButton.defaultSize.height * 1.6 + this.buttonMargin.y + (this.buttonSpacing*i)
                )
            }
        }

        this._surrenderButton.position.set(
            (common.STAGE_WIDTH - P3.View.width + this._surrenderButton.defaultSize.width) * 0.5 + this.buttonMargin.x,
            this._surrenderButton.defaultSize.height * 0.5 + this.buttonMargin.y
        );
        this._pauseButton.position.set(
            (common.STAGE_WIDTH + P3.View.width - this._pauseButton.defaultSize.width) * 0.5 - this.buttonMargin.width,
            this._pauseButton.defaultSize.height * 0.5 + this.buttonMargin.y
        );
        this._tutorialSkipButton.position.set(
            (common.STAGE_WIDTH - P3.View.width + this._tutorialSkipButton.defaultSize.width) * 0.5 + this.buttonMargin.x,
            P3.View.height - this._actionButton.height * 0.5 - this.buttonMargin.height
        );

        if(this._compass) {
            this._compass.position.set(
                ((common.STAGE_WIDTH - P3.View.width) * 0.5) + this.buttonMargin.x,
                horizontal ? 140 : this.buttonMargin.y
            );
        }

        this._uiHorizontal = horizontal;
    }

    update() {
        this._healthBar.update();
        this._questTracker.update();
        this._minigameTracker.update();
        this._dialogSystem.update();
        this._virtualJoystick.update();
        this._tutorialPointer.update();
        this._tutorialControls.update();

        const mainButtons = [
            this._mapButton, this._questButton, this._inventoryButton
        ];
        if (this._shopButton) {
            mainButtons.push(this._shopButton);
        }
        for (let i in mainButtons) {
            if (mainButtons[i].attractFx) {
                mainButtons[i].attractFx.update(window.dt);
            }
        }

        if (this._tutorialShopHighlight) {
            this._tutorialShopHighlight.update(window.dt);
        }

        if (this._compass) {
            this._compass.update();
        }
    }

    /**
     * @param {PIXI.Container} parentContainer 
     * @param {string} jsonFile 
     * @param {PIXI.Texture[]} textures 
     * @returns 
     */
    _createFx(parentContainer, jsonFile, textures=[]) {
        const emitter = new Emitter(parentContainer, textures, json(jsonFile));
        emitter.autoUpdate = false;
        return emitter;
    }

    /**
     * @param {bool} value 
     */
    setButtonsInteractiveState(value) {
        this._actionButton.interactive = value;
        this._mapButton.interactive = value;
        this._questButton.interactive = value;
        this._inventoryButton.interactive = value;
        this._pauseButton.interactive = value;

        if (this._shopButton)
            this._shopButton.interactive = value;
    }

    /**
     * @param {PIXI.Point} position
     */
    onVirtualJoystickStart(position) {
        const localPos = this.toLocal(new PIXI.Point(position.x, position.y));
        this._virtualJoystick.showJoystick(localPos);
    }

    onVirtualJoystickStop() {
        this._virtualJoystick.hideJoystick();
    }

    /**
     * @param {PIXI.Point} touch 
     */
    isPointIntersecting(touchPosition) {
        if (touchPosition === null) {
            return false;
        }

        for (let i = 0; i < this._joystickBlockers.length; ++i) {
            const blocker = this._joystickBlockers[i];
            let bounds = blocker.getBounds();
    
            if (touchPosition.x >= bounds.x && 
                touchPosition.y >= bounds.y && 
                touchPosition.x <= bounds.x + bounds.width && 
                touchPosition.y <= bounds.y + bounds.height) {
                    return true;
            }
        }

        return false;
    }

    /**
     * @param {PIXI.Sprite} target
     * @return {Promise}
     */
    animateToInventory(target) {
        return new Promise((resolve) => {
            target.position = this.toLocal(new PIXI.Point(target.x, target.y), target.parent);
            this.addChild(target);

            const tween = gsap.timeline({
                onComplete: () => {
                    this.animateInventoryButton().then();
                    target.destroy();
                    resolve();
                }
            });
            GameScene.active.addTween(tween);

            tween.add(gsap.to(target, 0.5, {
                x: this._inventoryButton.x,
                y: this._inventoryButton.y,
                ease: Power1.easeInOut
            }), 0);

            tween.add(gsap.to(target.scale, 0.24, {
                delay: 0.5 - 0.34,
                x: 0,
                y: 0,
                ease: Back.easeIn
            }), 0);
        });
    }

    /**
     * @param {PIXI.Texture} texture
     */
    showItemFound(texture) {
        return new Promise((resolve) => {
            if (!this._itemPopup.visible) {
                this._itemPopup.setItem(texture);
                this._itemPopup.animateIn(0.2).then(() => this._itemPopup.animateOut(1)).then(() => resolve());
            }
        });
    }

    /**
     * @param {number} type
     */
    setButtonLayout(type) {
        this._mapButton.visible = false;
        this._inventoryButton.visible = false;
        this._questButton.visible = false;
        if (this._shopButton) {
            this._shopButton.visible = false;
        }
        this._surrenderButton.visible = false;

        if (this._compass) {
            this._compass.visible = false;
        }

        switch (type) {
            case definitions.HUD_BUTTON_LAYOUT.DEFAULT: {
                this._mapButton.visible = true;
                this._inventoryButton.visible = true;
                this._questButton.visible = true;
                if (this._shopButton) {
                    this._shopButton.visible = true;
                }
                if (this._compass) {
                    this._compass.visible = true;
                }
                break;
            }
            case definitions.HUD_BUTTON_LAYOUT.MINIGAME:
            case definitions.HUD_BUTTON_LAYOUT.BOSS: {
                this._surrenderButton.visible = true;
                break;
            }
        }

        this._buttonLayout = type;
    }

    /**
     * @return {Promise}
     */
    animateInventoryButton() {
        return new Promise((resolve) => {
            gsap.killTweensOf(this._inventoryButton.scale);

            const tween = gsap.timeline({ onComplete: () => resolve() });
            GameScene.active.addTween(tween);

            tween.add(gsap.to(this._inventoryButton.scale, 0.22, {
                x: 1.2,
                y: 1.2,
                ease: Linear.easeNone
            }));
            tween.add(gsap.to(this._inventoryButton.scale, 0.18, {
                x: 1,
                y: 1,
                ease: Back.easeOut
            }));
        });
    }

    _animateUIButtons(out = false) {

        const buttons = [this._pauseButton];
        if (this._buttonLayout == definitions.HUD_BUTTON_LAYOUT.DEFAULT) {
            buttons.push(
                this._mapButton,
                this._questButton,
                this._inventoryButton);

            if (this._shopButton) {
                buttons.push(this._shopButton);
            }
        } else if (this._buttonLayout == definitions.HUD_BUTTON_LAYOUT.MINIGAME) {
            buttons.push(this._surrenderButton);
        }
        
        for (let i = 0; i < buttons.length; i++) {
            gsap.to(buttons[i].scale, 0.3, {delay:0.05*i, x:out ? 0 : 0.9, y:out ? 0: 0.9, ease:out ? Sine.easeIn : Sine.easeOut});
        }
    }

    animateUIButtonsIn() {
        this._animateUIButtons(false);
    }

    animateUIButtonsOut() {
        this._animateUIButtons(true);
    }

    addTutorialSkipButton() {
        this._tutorialSkipButton.visible = true;
    }
    removeTutorialSkipButton() {
        this._tutorialSkipButton.visible = false;
    }

    addCompass() {
        this._compass = new Compass();
        this.addChild(this._compass);
    }

    addTutorialShopHighlight() {

        if (this._shopButton == null) {
            return;
        }
        this._tutorialShopHighlight = new P3.MovieClip(new P3.MovieClipSequence([
            texture("tutorial_hand_point"), texture("tutorial_hand_press")
        ], "default"), "default", false);
        this._tutorialShopHighlight.animationSpeed = 1;
        this._tutorialShopHighlight.x = this._shopButton.x;
        this._tutorialShopHighlight.y = this._shopButton.y;
        this._tutorialShopHighlight.play(true);

        this.addChild(this._tutorialShopHighlight);
    }

    removeTutorialShopHighlight() {
        
        if (this._tutorialShopHighlight) {
            this.removeChild(this._tutorialShopHighlight);
        }
    }

    /**
     * @return {CoinCounterPanel}
     */
    get coinHolder() {
        return this._coinHolder;
    }

    /**
     * @return {HealthBar}
     */
    get healthBar() {
        return this._healthBar;
    }

    /**
     * @return {QuestTracker}
     */
    get questTracker() {
        return this._questTracker;
    }

    /**
     * @return {MinigameTracker}
     */
    get minigameTracker() {
        return this._minigameTracker;
    }

    /**
     * @return {TutorialPointer}
     */
    get tutorialPointer() {
        return this._tutorialPointer;
    }

    /**
     * @return {TutorialControls}
     */
    get tutorialControls() {
        return this._tutorialControls;
    }

    /**
     * @return {P3.Button}
     */
    get pauseButton() {
        return this._pauseButton;
    }

    /**
     * @return {P3.Button}
     */
    get actionButton() {
        return this._actionButton;
    }

    /**
     * @returns {P3.Button}
     */
    get mapButton() {
        return this._mapButton;
    }

    /**
     * @return {P3.Button}
     */
    get questButton() {
        return this._questButton;
    }

    /**
     * @return {P3.Button}
     */
    get inventoryButton() {
        return this._inventoryButton;
    }

    /**
     * @return {P3.Button}
     */
    get shopButton() {
        return this._shopButton;
    }

    /**
     * @return {P3.Button}
     */
    get surrenderButton() {
        return this._surrenderButton;
    }

    /**
     * @return {P3.Button}
     */
    get tutorialSkipButton() {
        return this._tutorialSkipButton;
    }

    /**
     * @returns {DialogSystem}
     */
    get dialogSystem() {
        return this._dialogSystem;
    }

    /**
     * @returns {Compass}
     */
    get compass() {
        return this._compass;
    }
    
    get horizontal() {
        return this._uiHorizontal;
    }
}